import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const fieldCls = "mt-2 w-full bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-md px-4 py-3 text-white";
const errText = (err) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg).join(" ") : "Something went wrong";
};

export default function ForgotPassword({ onBack }) {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      toast.success(r.data.message);
      setStep("reset");
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, otp, password });
      toast.success("Password updated. Please sign in.");
      onBack();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={step === "email" ? sendCode : reset} className="w-full max-w-md space-y-6 animate-fade-up" data-testid="forgot-form">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Admin recovery</div>
        <h2 className="font-serif text-3xl text-white mt-2">Forgot password</h2>
        <p className="text-slate-400 text-sm mt-2">
          {step === "email" ? "Enter your registered Admin email to receive a 6-digit reset code." : `Enter the code sent to ${email} and choose a new password. The code expires in 10 minutes.`}
        </p>
      </div>
      {step === "email" ? (
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">Registered Email</label>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={fieldCls} data-testid="forgot-email" />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">Reset Code</label>
            <input required inputMode="numeric" maxLength={6} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} className={`${fieldCls} tracking-[0.4em]`} data-testid="forgot-otp" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">New Password</label>
            <div className="relative">
              <input required minLength={6} type={show ? "text" : "password"} autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} className={`${fieldCls} pr-11`} data-testid="forgot-new-password" />
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setShow(s => !s)} data-testid="forgot-password-toggle" aria-label={show ? "Hide password" : "Show password"} className="absolute right-0 top-2 bottom-0 z-10 flex items-center px-3 text-slate-400 hover:text-[#D4AF37]">
                {show ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
      <button type="submit" disabled={busy} data-testid={step === "email" ? "forgot-send-btn" : "forgot-reset-btn"} className="w-full py-3 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold hover:bg-[#F2CE6E] transition-colors disabled:opacity-60">
        {busy ? "Please wait…" : step === "email" ? "Send Reset Code" : "Reset Password"}
      </button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={onBack} className="text-slate-400 hover:text-[#D4AF37]" data-testid="forgot-back">← Back to sign in</button>
        {step === "reset" && <button type="button" onClick={() => setStep("email")} className="text-slate-400 hover:text-[#D4AF37]" data-testid="forgot-resend">Resend code</button>}
      </div>
    </form>
  );
}
